import { Theme, css, useTheme } from '@emotion/react';

import { UIEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { InfiniteData, useInfiniteQuery, useQueryClient } from 'react-query';

import axios from 'axios';
import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import { ArrowDown, ArrowUp, Icon } from 'chatie-design-system/icon';

import Avatar, { AvatarSize } from '@components/common/Avatar';

import {
  LiveDiscoveryResponse,
  fetchLiveDiscovery,
  fetchLiveSession,
} from '@api/live';
import { LIVE_FEATURE_ENABLED, QUERY_KEYS } from '@constants';
import { useAuthStore } from '@contexts/auth';
import { useMe } from '@utils/hook';
import { getLiveKindLabel } from '@utils/liveMedia';
import { useWebView } from '@utils/webview';

const HOME_LIVE_COLLAPSED_STORAGE_KEY = 'homeLiveDiscoveryCollapsed';

const containerCSS = (collapsible: boolean) => (theme: Theme) =>
  css`
    ${collapsible && `border-top: 1px solid ${theme.border.light};`}
    border-bottom: 1px solid ${theme.border.light};
    padding: ${collapsible ? 0 : Spacing.medium}px 0;

    h5 {
      padding: 0 ${Spacing.medium}px;
      margin-bottom: ${Spacing.small}px;
    }
  `;

const toggleCSS = (theme: Theme) => css`
  ${Typography.body14};
  display: flex;
  width: 100%;
  min-height: 52px;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${Spacing.medium}px;
  color: ${theme.var.black};
  text-align: left;

  .heading {
    display: inline-flex;
    align-items: center;
    gap: ${Spacing.xsmall}px;
    font-weight: 700;
  }

  .live-dot {
    width: 7px;
    height: 7px;
    flex: 0 0 7px;
    border-radius: ${Radius.round}px;
    background-color: ${theme.colors.red500};
  }

  .summary {
    ${Typography.body12};
    display: inline-flex;
    align-items: center;
    gap: ${Spacing.xsmall}px;
    color: ${theme.var.gray500};
  }

  .summary-avatars {
    display: inline-flex;
    padding-left: 8px;
  }

  .summary-avatar {
    display: inline-flex;
    margin-left: -8px;
    overflow: hidden;
    border: 2px solid ${theme.var.white};
    border-radius: ${Radius.round}px;
  }
`;

const listCSS = (collapsible: boolean) => css`
  display: flex;
  gap: ${Spacing.small}px;
  padding: 0 ${Spacing.medium}px ${collapsible ? Spacing.small : 0}px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const itemCSS = (theme: Theme) => css`
  width: 84px;
  flex: 0 0 84px;
  text-align: center;
  cursor: pointer;

  .avatar-ring {
    display: inline-flex;
    padding: 3px;
    border: 2px solid ${theme.colors.red500};
    border-radius: ${Radius.round}px;
  }

  .avatar-crop {
    display: inline-flex;
    width: 44px;
    height: 44px;
    overflow: hidden;
    border-radius: 50%;
  }

  strong,
  small {
    display: block;
  }

  strong {
    ${Typography.body12};
    margin-top: ${Spacing.xsmall}px;
  }

  small {
    ${Typography.body11};
    color: ${theme.var.gray500};
    margin-top: 2px;
  }
`;

const moreCSS = (theme: Theme) => css`
  ${Typography.body12};
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  border-radius: ${Radius.round}px;
  background-color: ${theme.var.gray100};
  color: ${theme.var.gray600};
`;

type Props = {
  collapsible?: boolean;
};

const LiveDiscoveryList = ({ collapsible = false }: Props) => {
  const theme = useTheme();
  const { openPageOrMove } = useWebView();
  const { status } = useAuthStore();
  const { data: me } = useMe({ enabled: status === 'success' });
  const queryClient = useQueryClient();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const queryKey = useMemo(() => QUERY_KEYS.LIVE.DISCOVERY(me?.id), [me?.id]);
  const query = useInfiniteQuery<LiveDiscoveryResponse>(
    queryKey,
    ({ pageParam }) => fetchLiveDiscovery(pageParam as string | undefined),
    {
      enabled: LIVE_FEATURE_ENABLED && status === 'success' && !!me?.id,
      suspense: false,
      retry: false,
      staleTime: 30 * 1000,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
      getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    }
  );

  useEffect(() => {
    if (status !== 'success') {
      queryClient.removeQueries(['live', 'discovery']);
    }
  }, [queryClient, status]);

  useEffect(() => {
    if (!collapsible || typeof window === 'undefined') {
      return;
    }

    try {
      setIsCollapsed(
        window.localStorage.getItem(HOME_LIVE_COLLAPSED_STORAGE_KEY) === 'true'
      );
    } catch {
      setIsCollapsed(false);
    }
  }, [collapsible]);

  const sessions = query.data?.pages.reduce((items, page) => {
    const seen = new Set(items.map((item) => item.id));
    return items.concat(page.results.filter((item) => !seen.has(item.id)));
  }, [] as LiveDiscoveryResponse['results']);

  const removeSession = useCallback(
    (liveId: number) => {
      queryClient.setQueryData<InfiniteData<LiveDiscoveryResponse> | undefined>(
        queryKey,
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  results: page.results.filter((item) => item.id !== liveId),
                })),
              }
            : current
      );
    },
    [queryClient, queryKey]
  );

  const handleOpen = useCallback(
    async (liveId: number) => {
      try {
        const session = await fetchLiveSession(liveId);
        if (session.status !== 'live') {
          removeSession(liveId);
          return;
        }
        await openPageOrMove(`/lives/${liveId}`);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          removeSession(liveId);
        }
      }
    },
    [openPageOrMove, removeSession]
  );

  const loadNext = useCallback(async () => {
    if (!query.hasNextPage || query.isFetchingNextPage) {
      return;
    }
    try {
      await query.fetchNextPage();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        await queryClient.resetQueries(queryKey, { exact: true });
      }
    }
  }, [query, queryClient, queryKey]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    if (list.scrollWidth - list.scrollLeft - list.clientWidth < 120) {
      void loadNext();
    }
  };

  const handleToggle = useCallback(() => {
    setIsCollapsed((current) => {
      const next = !current;

      try {
        window.localStorage.setItem(
          HOME_LIVE_COLLAPSED_STORAGE_KEY,
          String(next)
        );
      } catch {
        // WebView 저장소를 사용할 수 없어도 현재 화면의 접기/펼치기는 유지한다.
      }

      return next;
    });
  }, []);

  if (
    !LIVE_FEATURE_ENABLED ||
    !query.isFetched ||
    !sessions ||
    sessions.length === 0
  ) {
    return null;
  }

  const sessionCountLabel = `${sessions.length}${query.hasNextPage ? '+' : ''}`;

  return (
    <section css={containerCSS(collapsible)} aria-label="진행 중인 라이브">
      {collapsible ? (
        <button
          type="button"
          css={toggleCSS}
          aria-controls="live-discovery-list"
          aria-expanded={!isCollapsed}
          aria-label={`라이브 목록 ${isCollapsed ? '펼치기' : '접기'}`}
          onClick={handleToggle}
        >
          <span className="heading">
            <span className="live-dot" aria-hidden="true" />
            지금 라이브 중
          </span>
          <span className="summary">
            {isCollapsed && (
              <span className="summary-avatars" aria-hidden="true">
                {sessions.slice(0, 3).map((session) => (
                  <span className="summary-avatar" key={session.id}>
                    <Avatar
                      id={session.author.id}
                      picture={session.author.picture ?? undefined}
                      size={AvatarSize.small}
                      hideDecoration
                    />
                  </span>
                ))}
              </span>
            )}
            <span>{sessionCountLabel}개</span>
            <Icon
              icon={isCollapsed ? ArrowDown : ArrowUp}
              size={Sizes.xsmall}
              color={theme.var.gray500}
            />
          </span>
        </button>
      ) : (
        <h5>지금 라이브 중</h5>
      )}
      <div
        id={collapsible ? 'live-discovery-list' : undefined}
        css={listCSS(collapsible)}
        hidden={isCollapsed}
        onScroll={handleScroll}
      >
        {!isCollapsed && (
          <>
            {sessions.map((session) => (
              <button
                key={session.id}
                type="button"
                css={itemCSS}
                onClick={() => void handleOpen(session.id)}
              >
                <span className="avatar-ring">
                  <span className="avatar-crop" data-live-avatar-crop="true">
                    <Avatar
                      id={session.author?.id}
                      picture={session.author?.picture ?? undefined}
                      size={AvatarSize.large}
                      hideDecoration
                    />
                  </span>
                </span>
                <strong className="ellipsis">
                  {session.author.nickname ?? '작가'}
                </strong>
                <small className="ellipsis">
                  {getLiveKindLabel(session.kind)} ·{' '}
                  {session.title || '라이브 중'}
                </small>
              </button>
            ))}
            {query.hasNextPage && (
              <button
                type="button"
                css={moreCSS}
                disabled={query.isFetchingNextPage}
                onClick={() => void loadNext()}
              >
                더보기
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default LiveDiscoveryList;
